import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import useAuth from '../../hooks/useAuth';
import { canDo } from '../../utils/canDo';
import { useWhatsApp } from '../../context/WhatsAppContext';

/* ── localStorage keys ──────────────────────────────────── */
const LS_MEET  = 'online_meet_url';
const LS_ZOOM  = 'online_zoom_url';
const LS_EXTRA = 'online_extra_links';

/* ── helpers ─────────────────────────────────────────────── */
function fmtDT(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
function fmtDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function toGCalDate(d) {
  // yyyymmddTHHmmss (local)
  if (!d) return '';
  const dt = new Date(d);
  return dt.toISOString().replace(/[-:]/g,'').split('.')[0] + 'Z';
}

function classifyTime(appt) {
  const now = new Date();
  const start = appt.start ? new Date(appt.start) : null;
  const end   = appt.end   ? new Date(appt.end)   : null;
  const st    = (appt.status || '').toLowerCase();

  if (st === 'inprogress' || st === 'in_progress') return 'ongoing';
  if (st === 'completed' || st === 'skipped')      return 'past';
  if (start && start > now)                         return 'upcoming';
  if (end   && end   < now)                         return 'past';
  if (start && start <= now)                        return 'ongoing';
  return 'upcoming';
}

const STATUS_STYLE = {
  ongoing:  { bg: '#dcfce7', color: '#16a34a', label: '● Ongoing' },
  upcoming: { bg: '#dbeafe', color: '#2563eb', label: '◷ Upcoming' },
  past:     { bg: '#f3f4f6', color: '#6b7280', label: '✓ Past' },
};

/* ── Google Calendar deep-link builder ──────────────────── */
function buildGCalUrl({ title, description, start, end, invitees }) {
  const base = 'https://calendar.google.com/calendar/r/eventedit';
  const params = new URLSearchParams({
    text: title || 'Online Session',
    dates: `${toGCalDate(start)}/${toGCalDate(end || new Date(new Date(start).getTime() + 60 * 60000))}`,
    details: description || '',
  });
  if (invitees && invitees.length > 0) {
    params.append('add', invitees.join(','));
  }
  return `${base}?${params.toString()}`;
}

/* ═══════════════════════════════════════════════════════════
   NEW EVENT MODAL  (Outlook-style)
═══════════════════════════════════════════════════════════ */
function NewEventModal({ onClose, onSave, auth, defaultInvitees, sendWhatsApp }) {
  const [title, setTitle]             = useState('');
  const [description, setDesc]        = useState('');
  const [startDt, setStartDt]         = useState('');
  const [endDt, setEndDt]             = useState('');
  const [isOnline, setIsOnline]       = useState(true);
  const [platform, setPlatform]       = useState('meet');
  const [meetingLink, setMeetingLink] = useState('');
  const [invitees, setInvitees]       = useState(defaultInvitees || '');
  const [branch, setBranch]           = useState('');
  const [patientCard, setPatientCard] = useState('');
  const [saving, setSaving]           = useState(false);

  const handleSave = async () => {
    if (!title.trim() || !startDt) { alert('Title and Start time are required.'); return; }
    setSaving(true);
    const start = new Date(startDt);
    const end   = endDt ? new Date(endDt) : new Date(start.getTime() + 60 * 60000);
    const invList = invitees.split(',').map(s => s.trim()).filter(Boolean);

    const newAppt = {
      title,
      name: title,
      description,
      start,
      end,
      date: start,
      status: 'scheduled',
      type: 'Event',
      isOnline,
      meetingLink: isOnline ? meetingLink : '',
      branch: branch || auth?.branch || 'MedicalYogaTherapyCentre',
      patientCard: patientCard || '',
      username: auth?.username || '',
      handoverTo: auth?.username || '',
      handoverFrom: auth?.username || '',
      invitees: invList,
      platform: isOnline ? platform : '',
    };

    try {
      // Use /add (simple endpoint, no patient lookup) since events may not have a patientCard
      await api.post(`${process.env.REACT_APP_URL}/appointments/add`, newAppt);
      // If online + invitees present, open Google Calendar to send invite
      if (isOnline && invList.length > 0) {
        const gcal = buildGCalUrl({ title, description, start, end, invitees: invList });
        window.open(gcal, '_blank', 'noopener,noreferrer');
      }
      // Auto-send WhatsApp meeting link to phone numbers (not emails)
      if (isOnline && meetingLink && sendWhatsApp) {
        const phones = invitees.split(',').map(s => s.trim()).filter(s => /^\d{7,15}$/.test(s.replace(/\D/g,'')));
        const dtStr = start.toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
        phones.forEach(p => {
          const cleaned = p.replace(/\D/g,'');
          sendWhatsApp(cleaned, `Hi, your online session "${title}" is scheduled for ${dtStr}. Join: ${meetingLink}`);
        });
      }
      onSave();
    } catch (e) {
      alert('Error creating event: ' + e.message);
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {/* Modal header */}
        <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '20px 24px', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: 17 }}>📅 New Event</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Title */}
          <div>
            <label style={labelStyle}>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Session title / event name" style={inputStyle} />
          </div>
          {/* Description */}
          <div>
            <label style={labelStyle}>Description / Notes</label>
            <textarea value={description} onChange={e => setDesc(e.target.value)} rows={3}
              placeholder="Add a description, agenda, or notes…"
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          {/* Start / End */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Start *</label>
              <input type="datetime-local" value={startDt} onChange={e => setStartDt(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>End</label>
              <input type="datetime-local" value={endDt} onChange={e => setEndDt(e.target.value)} style={inputStyle} />
            </div>
          </div>
          {/* Online / Offline toggle */}
          <div>
            <label style={labelStyle}>Mode</label>
            <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #d1d5db', width: 'fit-content' }}>
              <button onClick={() => setIsOnline(false)}
                style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: !isOnline ? '#2563eb' : '#f9fafb', color: !isOnline ? '#fff' : '#374151' }}>
                At Centre
              </button>
              <button onClick={() => setIsOnline(true)}
                style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: isOnline ? '#059669' : '#f9fafb', color: isOnline ? '#fff' : '#374151' }}>
                Online
              </button>
            </div>
          </div>
          {/* Platform + Meeting URL (if online) */}
          {isOnline && (
            <>
              <div>
                <label style={labelStyle}>Platform</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['meet','zoom','other'].map(p => (
                    <button key={p} onClick={() => setPlatform(p)}
                      style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, border: `2px solid ${platform===p?'#2563eb':'#d1d5db'}`, borderRadius: 8, cursor: 'pointer', background: platform===p?'#eff6ff':'#fff', color: platform===p?'#2563eb':'#374151', textTransform: 'capitalize' }}>
                      {p === 'meet' ? '📹 Meet' : p === 'zoom' ? '💻 Zoom' : '🔗 Other'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Meeting Link</label>
                <input value={meetingLink} onChange={e => setMeetingLink(e.target.value)}
                  placeholder={platform === 'meet' ? 'https://meet.google.com/xxx-xxxx-xxx' : platform === 'zoom' ? 'https://zoom.us/j/xxxxxxxxx' : 'https://…'}
                  style={inputStyle} />
              </div>
            </>
          )}
          {/* Invitees */}
          <div>
            <label style={labelStyle}>Invitees (email, comma-separated)</label>
            <input value={invitees} onChange={e => setInvitees(e.target.value)}
              placeholder="john@example.com, jane@example.com"
              style={inputStyle} />
            {isOnline && invitees.trim() && (
              <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>
                ✓ Google Calendar invite will be opened automatically after saving
              </div>
            )}
          </div>
          {/* Branch + Patient Card */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Branch / Location</label>
              <input value={branch} onChange={e => setBranch(e.target.value)} placeholder="Main Branch" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Patient Card (optional)</label>
              <input value={patientCard} onChange={e => setPatientCard(e.target.value)} placeholder="Card ID" style={inputStyle} />
            </div>
          </div>
          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 1, padding: '11px 0', background: 'linear-gradient(135deg,#2563eb,#059669)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : '✓ Create Event'}
            </button>
            <button onClick={onClose}
              style={{ padding: '11px 20px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EDIT EVENT MODAL
   Pre-fills all fields from an existing appointment.
   Supports: Save Changes, Send Update (WhatsApp), Delete.
═══════════════════════════════════════════════════════════ */
function toLocalDTInput(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditEventModal({ event, onClose, onSaved, onDeleted, sendWhatsApp }) {
  const [title,       setTitle]       = useState(event.title || event.name || '');
  const [description, setDesc]        = useState(event.description || event.sessionComments || '');
  const [startDt,     setStartDt]     = useState(toLocalDTInput(event.start));
  const [endDt,       setEndDt]       = useState(toLocalDTInput(event.end));
  const [isOnline,    setIsOnline]    = useState(!!event.isOnline);
  const [platform,    setPlatform]    = useState(event.platform || 'meet');
  const [meetingLink, setMeetingLink] = useState(event.meetingLink || '');
  const [invitees,    setInvitees]    = useState((event.invitees || []).join(', '));
  const [branch,      setBranch]      = useState(event.branch || '');
  const [status,      setStatus]      = useState(event.status || 'scheduled');
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [confirmDel,  setConfirmDel]  = useState(false);

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 };

  const handleSave = async () => {
    if (!title.trim() || !startDt) { alert('Title and Start are required.'); return; }
    setSaving(true);
    const start = new Date(startDt);
    const end   = endDt ? new Date(endDt) : new Date(start.getTime() + 60 * 60000);
    try {
      await api.post(`${process.env.REACT_APP_URL}/appointments/update/${event._id}`, {
        title, name: title, description,
        start, end, isOnline, meetingLink: isOnline ? meetingLink : '',
        branch, status, platform: isOnline ? platform : '',
        invitees: invitees.split(',').map(s => s.trim()).filter(Boolean),
        sessionComments: description,
      });
      onSaved();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // Send WhatsApp + open GCal to all participants
  const handleSendUpdate = () => {
    const invList  = invitees.split(',').map(s => s.trim()).filter(Boolean);
    const start    = new Date(startDt);
    const end      = endDt ? new Date(endDt) : new Date(start.getTime() + 60 * 60000);
    const dtStr    = start.toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    const msg      = `📅 Update: "${title}" on ${dtStr}${description ? '\n' + description : ''}${meetingLink ? '\nJoin: ' + meetingLink : ''}`;
    let sent = 0;
    invList.forEach(contact => {
      const phone = contact.replace(/\D/g, '');
      if (phone.length >= 7 && sendWhatsApp) {
        sendWhatsApp(phone, msg);
        sent++;
      }
    });
    // Also open Google Calendar update if online + email invitees
    if (isOnline) {
      const emails = invList.filter(c => c.includes('@'));
      if (emails.length > 0) {
        window.open(buildGCalUrl({ title, description, start, end, invitees: emails }), '_blank', 'noopener,noreferrer');
      }
    }
    alert(sent > 0 ? `WhatsApp update sent to ${sent} contact(s).` : 'No phone numbers found in invitees list.');
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) { alert('Please enter a reason.'); return; }
    setDeleting(true);
    try {
      await api.delete(`${process.env.REACT_APP_URL}/appointments/${event._id}`, { data: { reason: deleteReason } });
      onDeleted();
    } catch (e) { alert('Delete failed: ' + e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: 'linear-gradient(135deg,#374151,#1e3a5f)', padding: '18px 22px', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: 16 }}>✏️ Edit Event</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Title */}
          <div><label style={labelStyle}>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} /></div>
          {/* Description */}
          <div><label style={labelStyle}>Description / Notes</label>
            <textarea value={description} onChange={e => setDesc(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} /></div>
          {/* Start / End */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Start *</label>
              <input type="datetime-local" value={startDt} onChange={e => setStartDt(e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>End</label>
              <input type="datetime-local" value={endDt} onChange={e => setEndDt(e.target.value)} style={inputStyle} /></div>
          </div>
          {/* Status */}
          <div><label style={labelStyle}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              {['scheduled','inprogress','completed','pending','cancelled','skipped'].map(s => <option key={s}>{s}</option>)}
            </select></div>
          {/* Online toggle */}
          <div>
            <label style={labelStyle}>Mode</label>
            <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #d1d5db', width: 'fit-content' }}>
              <button onClick={() => setIsOnline(false)} style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: !isOnline ? '#2563eb' : '#f9fafb', color: !isOnline ? '#fff' : '#374151' }}>At Centre</button>
              <button onClick={() => setIsOnline(true)}  style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background:  isOnline ? '#059669' : '#f9fafb', color:  isOnline ? '#fff' : '#374151' }}>Online</button>
            </div>
          </div>
          {/* Platform + Link (optional, only when online) */}
          {isOnline && (
            <>
              <div>
                <label style={labelStyle}>Platform</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['meet','zoom','other'].map(p => (
                    <button key={p} onClick={() => setPlatform(p)}
                      style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: `2px solid ${platform===p?'#2563eb':'#d1d5db'}`, borderRadius: 8, cursor: 'pointer', background: platform===p?'#eff6ff':'#fff', color: platform===p?'#2563eb':'#374151' }}>
                      {p === 'meet' ? '📹 Meet' : p === 'zoom' ? '💻 Zoom' : '🔗 Other'}
                    </button>
                  ))}
                </div>
              </div>
              <div><label style={labelStyle}>Meeting Link <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <input value={meetingLink} onChange={e => setMeetingLink(e.target.value)} placeholder="https://meet.google.com/… or leave blank" style={inputStyle} /></div>
            </>
          )}
          {/* Invitees */}
          <div><label style={labelStyle}>Invitees — phone numbers or emails, comma-separated</label>
            <input value={invitees} onChange={e => setInvitees(e.target.value)} placeholder="9876543210, john@example.com, …" style={inputStyle} /></div>
          {/* Branch */}
          <div><label style={labelStyle}>Branch / Location</label>
            <input value={branch} onChange={e => setBranch(e.target.value)} style={inputStyle} /></div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 2, padding: '11px 0', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : '✓ Save Changes'}
            </button>
            <button onClick={handleSendUpdate}
              style={{ flex: 2, padding: '11px 0', background: '#059669', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              📲 Send Update
            </button>
            <button onClick={onClose}
              style={{ flex: 1, padding: '11px 0', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>

          {/* Delete section */}
          {!confirmDel ? (
            <button onClick={() => setConfirmDel(true)}
              style={{ padding: '8px 0', background: 'transparent', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              🗑 Delete this event
            </button>
          ) : (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 16px' }}>
              <label style={{ ...labelStyle, color: '#991b1b' }}>Reason for deletion *</label>
              <input value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Enter reason…" style={{ ...inputStyle, borderColor: '#fca5a5' }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={handleDelete} disabled={deleting}
                  style={{ flex: 1, padding: '9px 0', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  {deleting ? 'Deleting…' : 'Confirm Delete'}
                </button>
                <button onClick={() => setConfirmDel(false)}
                  style={{ flex: 1, padding: '9px 0', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function OnlineDashboard() {
  const { auth } = useAuth();
  const { sendWhatsApp } = useWhatsApp();

  /* ── state ─────────────────────────────────────────────── */
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [filter, setFilter]             = useState('ongoing_upcoming');
  const [typeFilter, setTypeFilter]     = useState('all');
  const [onlineOnly, setOnlineOnly]     = useState(true);
  const [showConfig, setShowConfig]     = useState(false);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selected, setSelected]         = useState(new Set());

  /* ── config (localStorage) ────────────────────────────── */
  const [meetUrl,  setMeetUrl]  = useState(() => localStorage.getItem(LS_MEET)  || '');
  const [zoomUrl,  setZoomUrl]  = useState(() => localStorage.getItem(LS_ZOOM)  || '');
  const [extraLinks, setExtraLinks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_EXTRA) || '[]'); }
    catch { return []; }
  });
  const [configMeet,  setConfigMeet]  = useState(meetUrl);
  const [configZoom,  setConfigZoom]  = useState(zoomUrl);
  const [configExtra, setConfigExtra] = useState(JSON.stringify(extraLinks, null, 2));

  /* ── fetch ─────────────────────────────────────────────── */
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const isAdmin = canDo(auth, 'online.edit');
      const url = isAdmin
        ? `${process.env.REACT_APP_URL}/appointments/`
        : `${process.env.REACT_APP_URL}/appointments/therapist/${auth.username}`;
      const res = await api.get(url);
      setAppointments(res.data || []);
    } catch (e) {
      console.error('OnlineDashboard fetch error:', e);
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  /* ── save config ──────────────────────────────────────── */
  const saveConfig = () => {
    localStorage.setItem(LS_MEET, configMeet);
    localStorage.setItem(LS_ZOOM, configZoom);
    try {
      const parsed = JSON.parse(configExtra);
      localStorage.setItem(LS_EXTRA, JSON.stringify(parsed));
      setExtraLinks(parsed);
    } catch (e) { /* ignore bad JSON */ }
    setMeetUrl(configMeet);
    setZoomUrl(configZoom);
    setShowConfig(false);
  };

  /* ── filtered list ─────────────────────────────────────── */
  const apptTypes = [...new Set(appointments.map(a => a.type).filter(Boolean))];

  const filtered = appointments.filter(a => {
    const tc = classifyTime(a);
    const timeOk =
      filter === 'all'              ? true :
      filter === 'ongoing_upcoming' ? (tc === 'ongoing' || tc === 'upcoming') :
      tc === filter;
    const typeOk   = typeFilter === 'all' || a.type === typeFilter;
    const onlineOk = !onlineOnly || a.isOnline === true;
    return timeOk && typeOk && onlineOk;
  }).sort((a, b) => {
    const order = { ongoing: 0, upcoming: 1, past: 2 };
    const ta = order[classifyTime(a)] ?? 1;
    const tb = order[classifyTime(b)] ?? 1;
    if (ta !== tb) return ta - tb;
    return new Date(a.start || 0) - new Date(b.start || 0);
  });

  /* ── counts (for stat cards — count from all, not filtered) */
  const onlineAppts = onlineOnly ? appointments.filter(a => a.isOnline === true) : appointments;
  const counts = { ongoing: 0, upcoming: 0, past: 0 };
  onlineAppts.forEach(a => { const k = classifyTime(a); counts[k] = (counts[k] || 0) + 1; });

  /* ── selection helpers ──────────────────────────────────── */
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(filtered.map(a => a._id)));
  const clearSel  = () => setSelected(new Set());

  const selectedAppts = filtered.filter(a => selected.has(a._id));

  /* ── build invite email list from selected ─────────────── */
  function getInviteeEmails(appts) {
    return appts
      .flatMap(a => [a.email, ...(a.invitees || [])])
      .filter(Boolean)
      .filter(e => e.includes('@'));
  }

  /* ── multi-select Meet/Zoom actions ─────────────────────── */
  const openMeetWithSelected = () => {
    const emails = getInviteeEmails(selectedAppts);
    const first  = selectedAppts[0] || {};
    const gcal   = buildGCalUrl({
      title: selectedAppts.length === 1 ? (first.name || first.title || 'Online Session') : `Group Session (${selectedAppts.length} patients)`,
      description: selectedAppts.map(a => a.name || a.title || a.patientCard).join(', '),
      start: first.start || new Date(),
      end:   first.end,
      invitees: emails,
    });
    window.open(gcal, '_blank', 'noopener,noreferrer');
  };

  const openZoomWithSelected = () => {
    // Zoom doesn't support URL-based invite; open configured Zoom URL
    const url = selectedAppts[0]?.meetingLink || zoomUrl;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else alert('No Zoom URL configured. Set it in Configure Links.');
  };

  /* ── row join URL helper ─────────────────────────────────── */
  function rowMeetUrl(appt)  { return appt.meetingLink || meetUrl || ''; }
  function rowZoomUrl(appt)  { return appt.meetingLink || zoomUrl || ''; }

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>

      {/* ── HEADER ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 22, color: '#1e3a5f' }}>🌐 Online Consultations</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Video sessions, meetings and online events</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowNewEvent(true)}
            style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#2563eb,#059669)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            + New Event
          </button>
          <button onClick={fetchAppointments}
            style={{ padding: '7px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
          <button onClick={() => setShowConfig(v => !v)}
            style={{ padding: '7px 14px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            ⚙ Configure
          </button>
        </div>
      </div>

      {/* ── CONFIG PANEL ───────────────────────────────────── */}
      {showConfig && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px', marginBottom: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#1e3a5f', marginBottom: 16 }}>Configure Meeting Links</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Google Meet URL</label>
              <input value={configMeet} onChange={e => setConfigMeet(e.target.value)}
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Zoom URL</label>
              <input value={configZoom} onChange={e => setConfigZoom(e.target.value)}
                placeholder="https://zoom.us/j/xxxxxxxxx"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
              Extra Links (JSON: <code style={{ fontSize: 11 }}>[{'{'}label:"Teams",url:"https://..."{'}'}]</code>)
            </label>
            <textarea value={configExtra} onChange={e => setConfigExtra(e.target.value)} rows={3}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={saveConfig} style={{ padding: '9px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>✓ Save</button>
            <button onClick={() => setShowConfig(false)} style={{ padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── QUICK LAUNCH BUTTONS ────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {meetUrl && (
          <a href={meetUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: '#fff', border: '2px solid #4285f4', borderRadius: 14, textDecoration: 'none', boxShadow: '0 2px 10px rgba(66,133,244,0.18)' }}>
            <svg width="26" height="26" viewBox="0 0 48 48" style={{ display:'block' }}>
              <path d="M44 24c0-1.3-.1-2.5-.4-3.7H24v7h11.3c-.5 2.6-2 4.8-4.2 6.3v5.2h6.8C41.4 35.5 44 30.2 44 24z" fill="#4285F4"/>
              <path d="M24 44c5.7 0 10.5-1.9 14-5.1l-6.8-5.2c-1.9 1.3-4.3 2-7.2 2-5.5 0-10.2-3.7-11.9-8.7H5.1v5.4C8.6 39.7 15.8 44 24 44z" fill="#34A853"/>
              <path d="M12.1 27c-.4-1.3-.7-2.6-.7-4s.3-2.7.7-4V13.6H5.1C3.8 16.2 3 19 3 24s.8 7.8 2.1 10.4L12.1 27z" fill="#FBBC04"/>
              <path d="M24 10.3c3.1 0 5.9 1.1 8.1 3.2l6-6C34.5 4 29.7 2 24 2 15.8 2 8.6 6.3 5.1 13.6l7 5.4C13.8 14 18.5 10.3 24 10.3z" fill="#EA4335"/>
            </svg>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#1a1a2e' }}>Google Meet</div>
              <div style={{ fontSize: 10, color: '#4285f4', fontWeight: 600 }}>1-click join ↗</div>
            </div>
          </a>
        )}
        {zoomUrl && (
          <a href={zoomUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: '#fff', border: '2px solid #2d8cff', borderRadius: 14, textDecoration: 'none', boxShadow: '0 2px 10px rgba(45,140,255,0.18)' }}>
            <svg width="26" height="26" viewBox="0 0 100 100" style={{ display:'block' }}>
              <rect width="100" height="100" rx="22" fill="#2D8CFF"/>
              <path d="M15 35h43c3.3 0 6 2.7 6 6v24c0 3.3-2.7 6-6 6H21c-3.3 0-6-2.7-6-6V35z" fill="#fff"/>
              <path d="M64 48l17-12v28L64 52v-4z" fill="#fff"/>
            </svg>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#1a1a2e' }}>Zoom</div>
              <div style={{ fontSize: 10, color: '#2d8cff', fontWeight: 600 }}>1-click join ↗</div>
            </div>
          </a>
        )}
        {extraLinks.map((lnk, i) => (
          <a key={i} href={lnk.url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: '#fff', border: '2px solid #8b5cf6', borderRadius: 14, textDecoration: 'none', boxShadow: '0 2px 10px rgba(139,92,246,0.18)' }}>
            <span style={{ fontSize: 24 }}>🔗</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#1a1a2e' }}>{lnk.label}</div>
              <div style={{ fontSize: 10, color: '#8b5cf6', fontWeight: 600 }}>1-click join ↗</div>
            </div>
          </a>
        ))}
        {!meetUrl && !zoomUrl && extraLinks.length === 0 && (
          <div style={{ padding: '12px 20px', background: '#f8fafc', border: '2px dashed #e2e8f0', borderRadius: 14, fontSize: 13, color: '#94a3b8' }}>
            ⚙ Click "Configure" to set your Google Meet / Zoom URLs
          </div>
        )}
      </div>

      {/* ── STAT CARDS ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'ongoing',  label: 'Ongoing',  bg: 'linear-gradient(135deg,#16a34a,#0d9488)', count: counts.ongoing },
          { key: 'upcoming', label: 'Upcoming', bg: 'linear-gradient(135deg,#2563eb,#0891b2)', count: counts.upcoming },
          { key: 'past',     label: 'Past',     bg: 'linear-gradient(135deg,#64748b,#475569)', count: counts.past },
        ].map(c => (
          <div key={c.key} onClick={() => setFilter(c.key)}
            style={{ flex: 1, minWidth: 100, background: c.bg, color: '#fff', borderRadius: 12, padding: '14px 18px', cursor: 'pointer', opacity: filter === c.key ? 1 : 0.7, transition: 'opacity 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1 }}>{c.count}</div>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 3 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── FILTERS ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { v: 'ongoing_upcoming', label: 'Ongoing + Upcoming' },
          { v: 'ongoing',          label: 'Ongoing' },
          { v: 'upcoming',         label: 'Upcoming' },
          { v: 'past',             label: 'Past' },
          { v: 'all',              label: 'All' },
        ].map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            style={{ padding: '6px 14px', borderRadius: 20, fontWeight: 600, fontSize: 12, cursor: 'pointer', border: 'none',
              background: filter === f.v ? '#1e3a5f' : '#f1f5f9',
              color: filter === f.v ? '#fff' : '#374151' }}>
            {f.label}
          </button>
        ))}
        {/* Online only toggle */}
        <button onClick={() => setOnlineOnly(v => !v)}
          style={{ padding: '6px 14px', borderRadius: 20, fontWeight: 600, fontSize: 12, cursor: 'pointer',
            border: `2px solid ${onlineOnly ? '#059669' : '#d1d5db'}`,
            background: onlineOnly ? '#d1fae5' : '#fff',
            color: onlineOnly ? '#059669' : '#374151' }}>
          🌐 {onlineOnly ? 'Online Only' : 'All (incl. At Centre)'}
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Type:</span>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, background: '#fff' }}>
            <option value="all">All Types</option>
            {apptTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* ── SELECTION TOOLBAR ───────────────────────────────── */}
      {selected.size > 0 && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{selected.size} selected</span>
          {meetUrl && (
            <button onClick={openMeetWithSelected}
              style={{ padding: '6px 14px', background: '#4285f4', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              📹 Open Meet + Invite
            </button>
          )}
          {zoomUrl && (
            <button onClick={openZoomWithSelected}
              style={{ padding: '6px 14px', background: '#2d8cff', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              💻 Open Zoom
            </button>
          )}
          <button onClick={clearSel}
            style={{ padding: '6px 12px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
            ✕ Clear
          </button>
          <button onClick={selectAll} style={{ padding: '6px 12px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
            ☐ Select All ({filtered.length})
          </button>
        </div>
      )}
      {selected.size === 0 && filtered.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={selectAll} style={{ padding: '4px 12px', background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, cursor: 'pointer' }}>
            ☐ Select All ({filtered.length})
          </button>
        </div>
      )}

      {/* ── APPOINTMENTS LIST ───────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 15 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>No appointments found</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>
            {onlineOnly ? 'No online appointments. Toggle "All (incl. At Centre)" to see all.' : 'Try a different filter.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(appt => {
            const tc = classifyTime(appt);
            const ss = STATUS_STYLE[tc] || STATUS_STYLE.upcoming;
            const isSel = selected.has(appt._id);
            const apptMeet = rowMeetUrl(appt);
            const apptZoom = rowZoomUrl(appt);
            return (
              <div key={appt._id}
                onClick={() => toggleSelect(appt._id)}
                style={{
                  background: isSel ? '#eff6ff' : '#fff',
                  borderRadius: 14, padding: '14px 18px',
                  border: `1px solid ${isSel ? '#93c5fd' : tc === 'ongoing' ? '#bbf7d0' : tc === 'upcoming' ? '#bfdbfe' : '#e5e7eb'}`,
                  borderLeft: `4px solid ${isSel ? '#2563eb' : tc === 'ongoing' ? '#16a34a' : tc === 'upcoming' ? '#2563eb' : '#9ca3af'}`,
                  boxShadow: isSel ? '0 2px 12px rgba(37,99,235,0.13)' : '0 1px 6px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Checkbox */}
                  <div style={{ paddingTop: 2, flexShrink: 0 }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isSel ? '#2563eb' : '#d1d5db'}`, background: isSel ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isSel && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1, fontWeight: 900 }}>✓</span>}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: '#1e3a5f' }}>
                        {appt.name || appt.title || appt.patientCard || '—'}
                      </span>
                      <span style={{ background: ss.bg, color: ss.color, fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px' }}>
                        {ss.label}
                      </span>
                      <span style={{ background: '#f1f5f9', color: '#475569', fontSize: 10, fontWeight: 600, borderRadius: 6, padding: '2px 7px' }}>
                        {appt.type || 'Appointment'}
                      </span>
                      {appt.isOnline && (
                        <span style={{ background: '#d1fae5', color: '#059669', fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '2px 7px' }}>
                          🌐 Online
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                      <span>👤 {appt.username || '—'}</span>
                      {appt.start && <span>📅 {fmtDT(appt.start)}</span>}
                      {appt.end   && <span>⏱ Until {fmtDate(appt.end)}</span>}
                      {appt.branch && <span>🏢 {appt.branch}</span>}
                      {appt.patientCard && <span>🪪 {appt.patientCard}</span>}
                    </div>
                    {appt.sessionComments && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', background: '#f8fafc', borderRadius: 6, padding: '4px 8px' }}>
                        💬 {appt.sessionComments}
                      </div>
                    )}
                  </div>
                  {/* Per-row action buttons */}
                  <div style={{ display: 'flex', gap: 6, alignSelf: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {/* Edit button — always visible */}
                    <button onClick={() => setEditingEvent(appt)}
                      style={{ padding: '7px 11px', background: '#f8fafc', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      ✏️ Edit
                    </button>
                    {/* Join buttons — only for online */}
                    {appt.isOnline && apptMeet && (
                      <a href={apptMeet} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#4285f4', color: '#fff', borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                        📹 Meet
                      </a>
                    )}
                    {appt.isOnline && apptZoom && apptZoom !== apptMeet && (
                      <a href={apptZoom} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#2d8cff', color: '#fff', borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                        💻 Zoom
                      </a>
                    )}
                    {appt.isOnline && (appt.email || (appt.invitees && appt.invitees.length > 0)) && (
                      <a href={buildGCalUrl({ title: appt.name || appt.title, start: appt.start, end: appt.end, invitees: [appt.email, ...(appt.invitees||[])].filter(Boolean) })}
                        target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                        📧 Invite
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── NEW EVENT MODAL ──────────────────────────────────── */}
      {showNewEvent && (
        <NewEventModal
          auth={auth}
          sendWhatsApp={sendWhatsApp}
          defaultInvitees={selectedAppts.flatMap(a => [a.email, ...(a.invitees||[])]).filter(Boolean).join(', ')}
          onClose={() => setShowNewEvent(false)}
          onSave={() => { setShowNewEvent(false); fetchAppointments(); }}
        />
      )}

      {/* ── EDIT EVENT MODAL ──────────────────────────────────── */}
      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          sendWhatsApp={sendWhatsApp}
          onClose={() => setEditingEvent(null)}
          onSaved={() => { setEditingEvent(null); fetchAppointments(); }}
          onDeleted={() => { setEditingEvent(null); fetchAppointments(); }}
        />
      )}
    </div>
  );
}
